// hooks/useNotificationSignalR.js
import { useCallback, useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import { useQueryClient } from '@tanstack/react-query';

// Singleton pattern ile tek connection yönet
class SignalRManager {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.isConnecting = false;
    this.currentUserId = null;
    this.reconnectTimeout = null;
    this.eventHandlerRegistered = false;
    this.queryClient = null;
  }

  setQueryClient(queryClient) {
    this.queryClient = queryClient;
  }

  async cleanup() {
    try {
      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      if (this.connection) {
        if (this.eventHandlerRegistered) {
          this.connection.off('ReloadNotifications');
          this.eventHandlerRegistered = false;
        }

        if (
          this.connection.state === signalR.HubConnectionState.Connected ||
          this.connection.state === signalR.HubConnectionState.Connecting
        ) {
          await this.connection.stop();
        }

        this.connection = null;
      }

      this.isConnected = false;
      this.isConnecting = false;
      this.currentUserId = null;
    } catch (error) {
      void error;
    }
  }

  async connect(userId) {
    try {
      if (!userId) {
        await this.cleanup();
        return;
      }

      // Aynı userId ile zaten bağlıysa tekrar bağlanma
      if (
        this.currentUserId === userId &&
        this.isConnected &&
        this.connection &&
        this.connection.state === signalR.HubConnectionState.Connected
      ) {
        return;
      }

      if (this.isConnecting) {
        return;
      }

      // Eski bağlantıyı temizle
      if (this.connection) {
        await this.cleanup();
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      this.isConnecting = true;

      // WebSocket URL'ini oluştur
      const wsUrl = `${import.meta.env.VITE_WS_BASE}/ws/v1/notificationhub?userId=${userId}`;

      // Yeni bağlantı oluştur
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(wsUrl, {
          transport: signalR.HttpTransportType.WebSockets,
          skipNegotiation: true,
        })
        .withAutomaticReconnect([1000, 3000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Error)
        .build();

      // SADECE ReloadNotifications event'ini dinle
      if (!this.eventHandlerRegistered) {
        connection.on('ReloadNotifications', () => {
          // QueryClient varsa direkt invalidate et
          if (this.queryClient) {
            // HEM ana notification query'lerini HEM de navbar query'sini invalidate et
            this.queryClient.invalidateQueries({
              queryKey: ['notifications', 'list'],
            });
            this.queryClient.invalidateQueries({
              queryKey: ['notifications', 'navbar'],
            });
          }
        });
        this.eventHandlerRegistered = true;
      }

      // Bağlantı durumu değişikliklerini dinle
      connection.onreconnecting(() => {
        this.isConnected = false;
        this.isConnecting = false;
      });

      connection.onreconnected(() => {
        this.isConnected = true;
        this.isConnecting = false;
      });

      connection.onclose((error) => {
        this.isConnected = false;
        this.isConnecting = false;
        this.eventHandlerRegistered = false;

        if (error) {
          void error.message;
        }
      });

      // Bağlantıyı başlat
      await connection.start();

      this.connection = connection;
      this.isConnected = true;
      this.isConnecting = false;
      this.currentUserId = userId;
    } catch (error) {
      this.isConnected = false;
      this.isConnecting = false;
      this.eventHandlerRegistered = false;

      // Hata durumunda otomatik retry
      if (userId) {
        this.reconnectTimeout = setTimeout(() => {
          this.connect(userId);
        }, 5000);
      }
      void error;
    }
  }

  async sendTestNotification(userId) {
    try {
      if (
        !userId ||
        !this.connection ||
        this.connection.state !== signalR.HubConnectionState.Connected
      ) {
        return;
      }

      await this.connection.invoke('SendTestNotification', userId);
    } catch (error) {
      void error;
    }
  }
}

// Global singleton instance
const signalRManager = new SignalRManager();

// Page unload'da cleanup
window.addEventListener('beforeunload', () => {
  signalRManager.cleanup();
});

export const useNotificationSignalR = (userId) => {
  const queryClient = useQueryClient();

  // QueryClient'ı manager'a set et
  useEffect(() => {
    signalRManager.setQueryClient(queryClient);
  }, [queryClient]);

  // Component mount/unmount
  useEffect(() => {
    if (userId) {
      signalRManager.connect(userId);
    }

    return () => {
      // Component unmount'ta cleanup yapma - multiple component için
    };
  }, [userId]);

  // Test notification gönder
  const sendTestNotification = useCallback(() => {
    signalRManager.sendTestNotification(userId);
  }, [userId]);

  return {
    connection: signalRManager.connection,
    isConnected: signalRManager.isConnected,
    connect: () => signalRManager.connect(userId),
    disconnect: () => signalRManager.cleanup(),
    sendTestNotification,
  };
};
